import { Component, ElementRef, Inject, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { EditorMatrixDialogComponent } from '@components/editor-matrix/editor-matrix-dialog.component';
import { ElementConditionService } from '@service/element-condition.service';
import { ElementConditionViewData } from 'icreate/common/models/element-condition';
import { Vessel } from 'icreate/common/models/vessel';
import { TriggerConditionLogicDialogComponent } from '../trigger-condition-logic-dialog/trigger-condition-logic-dialog.component';
import { lastValueFrom, Subject, takeUntil } from 'rxjs';
import { ElementConditionSocketService } from '@service/element-condition-socket.service';

@Component({
    selector: 'app-trigger-condition-dialog',
    templateUrl: './trigger-condition-dialog.component.html',
    styleUrls: ['./trigger-condition-dialog.component.css'],
    standalone: false,
})
export class TriggerConditionDialogComponent implements OnInit, OnDestroy {
    readonly LOGIC_DIALOG_RELATIVE_LEFT_POSITION = -342;

    isLoading: boolean = false;
    elementConditionList: ElementConditionViewData[] = [];
    selectedElementCondition: ElementConditionViewData;

    @ViewChild('container') container: ElementRef;

    private destroySubscriptionSubject$ = new Subject<void>();

    constructor(
        @Inject(MAT_DIALOG_DATA)
        public data: {
            vessel: Vessel;
            elementId: string;
        },
        private elementConditionService: ElementConditionService,
        private dialogRef: MatDialogRef<TriggerConditionDialogComponent>,
        private dialog: MatDialog,
        private elementConditionSocketService: ElementConditionSocketService
    ) {}

    async ngOnInit() {
        try {
            this.isLoading = true;

            if (!this.data.elementId) throw new Error('Element ID is required');

            const elementConditions = await this.elementConditionService.getByElementId(this.data.elementId);
            this.elementConditionList = elementConditions;
        } catch (error) {
            console.log(error);
        } finally {
            this.isLoading = false;
        }

        this.elementConditionSocketService
            .elementConditionUpdated$()
            .pipe(takeUntil(this.destroySubscriptionSubject$))
            .subscribe((event: { conditionList: ElementConditionViewData[]; elementId: string }) => {
                const { conditionList, elementId } = event;

                if (this.data.elementId === elementId) {
                    this.elementConditionList = [...conditionList];
                }
            });
    }

    ngOnDestroy(): void {
        this.destroySubscriptionSubject$.next();
        this.destroySubscriptionSubject$.complete();
    }

    openMatrixDialog() {
        this.dialog.open(EditorMatrixDialogComponent, {
            data: {
                vessel: this.data.vessel,
                elementId: this.data.elementId,
            },
            disableClose: true,
        });
    }

    async openConditionLogicDialog(condition: ElementConditionViewData) {
        this.selectedElementCondition = condition;
        const rect = this.container.nativeElement.getBoundingClientRect();
        try {
            const dialogRef = this.dialog.open(TriggerConditionLogicDialogComponent, {
                data: {
                    vessel: this.data.vessel,
                    condition: { ...condition },
                    elementId: this.data.elementId,
                },
                backdropClass: 'transparent-backdrop',
                position: {
                    top: `${rect.y}px`,
                    left: `${rect.x + this.LOGIC_DIALOG_RELATIVE_LEFT_POSITION}px`,
                },
            });

            await lastValueFrom(dialogRef.afterClosed());
        } catch (error) {
            console.log(error);
        } finally {
            this.selectedElementCondition = undefined;
        }
    }

    selectElementCondition(condition: ElementConditionViewData) {
        this.close(condition);
    }

    close(result) {
        this.dialogRef.close(result);
    }
}
